import { OktaAuth } from '@okta/okta-auth-js';
import { envConfig } from '../config/env.config';

export interface SessionConfig {
  sessionTimeoutMinutes: number;
  checkIntervalMinutes: number;
}

export interface SessionInfo {
  isActive: boolean;
  timeUntilExpiration: number | null; // in seconds
  lastActivity: Date | null;
  sessionStartTime: Date | null;
}

export class SessionService {
  private oktaAuth: OktaAuth;
  private config: SessionConfig;
  private sessionTimer: NodeJS.Timeout | null = null;
  private sessionStartTime: Date | null = null;
  private lastActivity: Date | null = null;

  constructor(oktaAuth: OktaAuth, config?: Partial<SessionConfig>) {
    this.oktaAuth = oktaAuth;
    this.config = {
      sessionTimeoutMinutes: envConfig.sessionTimeoutMinutes,
      checkIntervalMinutes: 1, // Check every minute
      ...config,
    };
  }

  /**
   * Start session monitoring
   */
  public startSessionMonitoring(): void {
    this.sessionStartTime = new Date();
    this.lastActivity = new Date();
    
    // Set up activity listeners
    this.setupActivityListeners();
    
    this.scheduleSessionCheck();
  }

  /**
   * Stop session monitoring
   */
  public stopSessionMonitoring(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    
    // Remove activity listeners
    this.removeActivityListeners();
    
    this.sessionStartTime = null;
    this.lastActivity = null;
  }

  /**
   * Update last activity time (call this when user is active)
   */
  public updateActivity(): void {
    this.lastActivity = new Date();
  }

  /**
   * Get current session information
   */
  public getSessionInfo(): SessionInfo {
    const isActive = this.isSessionActive();
    const timeUntilExpiration = this.getTimeUntilSessionExpiration();
    
    return {
      isActive,
      timeUntilExpiration,
      lastActivity: this.lastActivity,
      sessionStartTime: this.sessionStartTime,
    };
  }

  /**
   * Check if session is still active based on Okta tokens and organizational policies
   */
  public isSessionActive(): boolean {
    try {
      // Check if user is authenticated in Okta
      const authState = this.oktaAuth.authStateManager.getAuthState();
      if (!authState?.isAuthenticated) {
        return false;
      }

      // Check if session has exceeded organizational timeout
      if (this.hasSessionExpired()) {
        return false;
      }

      // Check if tokens are still valid
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = tokenManager.get('accessToken');
      const idToken = tokenManager.get('idToken');

      // If we have no tokens, session is not active
      if (!accessToken && !idToken) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking session status:', error);
      return false;
    }
  }

  /**
   * Check if session has expired based on idle time
   */
  public hasSessionExpired(): boolean {
    if (!this.lastActivity) {
      return true;
    }

    const now = new Date();
    const idleDurationMs = now.getTime() - this.lastActivity.getTime();
    const idleTimeoutMs = this.config.sessionTimeoutMinutes * 60 * 1000;

    return idleDurationMs >= idleTimeoutMs;
  }

  /**
   * Get time until session expiration based on idle time in seconds
   */
  public getTimeUntilSessionExpiration(): number | null {
    if (!this.lastActivity) {
      return null;
    }

    const now = new Date();
    const idleDurationMs = now.getTime() - this.lastActivity.getTime();
    const idleTimeoutMs = this.config.sessionTimeoutMinutes * 60 * 1000;
    const remainingMs = idleTimeoutMs - idleDurationMs;

    return Math.max(0, Math.floor(remainingMs / 1000));
  }

  /**
   * Get time until token expiration from Okta
   */
  public async getTimeUntilTokenExpiration(): Promise<number | null> {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');
      const idToken = await tokenManager.get('idToken');

      if (!accessToken && !idToken) {
        return null;
      }

      // Get the earliest expiration time
      let earliestExpiration = Infinity;

      if (accessToken?.expiresAt) {
        earliestExpiration = Math.min(earliestExpiration, accessToken.expiresAt);
      }

      if (idToken?.expiresAt) {
        earliestExpiration = Math.min(earliestExpiration, idToken.expiresAt);
      }

      if (earliestExpiration === Infinity) {
        return null;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      return Math.max(0, earliestExpiration - currentTime);
    } catch (error) {
      console.error('Error getting token expiration time:', error);
      return null;
    }
  }

  /**
   * Get the effective session timeout (minimum of organizational policy and token expiration)
   */
  public async getEffectiveSessionTimeout(): Promise<number | null> {
    const organizationalTimeout = this.getTimeUntilSessionExpiration();
    const tokenTimeout = await this.getTimeUntilTokenExpiration();

    if (organizationalTimeout === null && tokenTimeout === null) {
      return null;
    }

    if (organizationalTimeout === null) {
      return tokenTimeout;
    }

    if (tokenTimeout === null) {
      return organizationalTimeout;
    }

    // Return the minimum (most restrictive) timeout
    return Math.min(organizationalTimeout, tokenTimeout);
  }

  /**
   * Extend session by refreshing tokens (if possible)
   */
  public async extendSession(): Promise<boolean> {
    try {
      // Check if we can refresh tokens
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');
      const idToken = await tokenManager.get('idToken');

      if (!accessToken && !idToken) {
        return false;
      }

      // Try to refresh tokens
      const refreshPromises: Promise<any>[] = [];
      
      if (accessToken) {
        refreshPromises.push(tokenManager.renew('accessToken'));
      }

      if (idToken) {
        refreshPromises.push(tokenManager.renew('idToken'));
      }

      await Promise.all(refreshPromises);
      
      // Update session start time to extend organizational timeout
      this.sessionStartTime = new Date();
      this.lastActivity = new Date();
      
      return true;

    } catch (error) {
      return false;
    }
  }

  /**
   * Schedule periodic session checks
   */
  private scheduleSessionCheck(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    const checkIntervalMs = this.config.checkIntervalMinutes * 60 * 1000;

    this.sessionTimer = setTimeout(async () => {
      try {
        await this.performSessionCheck();
      } catch (error) {
        console.error('Error during session check:', error);
      }
      
      // Schedule next check if session is still active
      if (this.isSessionActive()) {
        this.scheduleSessionCheck();
      }
    }, checkIntervalMs);
  }

  /**
   * Perform session validity check
   */
  private async performSessionCheck(): Promise<void> {
    const sessionInfo = this.getSessionInfo();
    
    if (!sessionInfo.isActive) {
      this.handleSessionExpired();
      return;
    }

    const effectiveTimeout = await this.getEffectiveSessionTimeout();
    
    if (effectiveTimeout !== null && effectiveTimeout <= 0) {
      this.handleSessionExpired();
      return;
    }
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpired(): void {
    this.stopSessionMonitoring();
    
    // Emit a custom event that the AuthContext can listen to
    const sessionExpiredEvent = new CustomEvent('sessionExpired', {
      detail: {
        reason: 'timeout',
        sessionDuration: this.sessionStartTime ?
          (new Date().getTime() - this.sessionStartTime.getTime()) / 1000 : 0
      }
    });
    window.dispatchEvent(sessionExpiredEvent);
  }

  /**
   * Set up activity listeners to track user activity
   */
  private setupActivityListeners(): void {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, true);
    });
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity, true);
    });
  }

  /**
   * Handle user activity - update last activity time
   */
  private handleUserActivity = (): void => {
    this.updateActivity();
  }

  /**
   * Get session statistics for debugging
   */
  public getSessionStats(): {
    sessionDurationSeconds: number | null;
    timeUntilIdleTimeout: number | null;
    configuredTimeoutMinutes: number;
    idleDurationSeconds: number | null;
  } {
    const sessionDurationSeconds = this.sessionStartTime ?
      Math.floor((new Date().getTime() - this.sessionStartTime.getTime()) / 1000) : null;
    
    const idleDurationSeconds = this.lastActivity ?
      Math.floor((new Date().getTime() - this.lastActivity.getTime()) / 1000) : null;
    
    return {
      sessionDurationSeconds,
      timeUntilIdleTimeout: this.getTimeUntilSessionExpiration(),
      configuredTimeoutMinutes: this.config.sessionTimeoutMinutes,
      idleDurationSeconds,
    };
  }
}

export default SessionService;